/**
 * /automation-accounts — platform-scoped route (primary mount).
 *
 * URL-driven list state (show_inactive) per the CLAUDE.md rule
 * that URL is the source of truth for list/filter state. Owns
 * the literal `<Link>` + `navigate` targets so the feature page
 * stays route-agnostic.
 */

import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  fetchAutomationAccounts,
  scopeKey,
} from "@/features/automation-accounts/queries";
import {
  automationAccountsListSearchSchema,
  type AutomationAccountsListSearch,
} from "@/features/automation-accounts/schemas";
import { queryKeys } from "@/lib/query-keys";
import { AutomationAccountsPage } from "@/features/automation-accounts/pages/automation-accounts-page";

function PlatformAutomationAccountsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const showInactive = !!search.show_inactive;
  return (
    <AutomationAccountsPage
      tenantID={null}
      scope="platform"
      title="Automation accounts"
      description="Non-interactive admin identities for setup scripts, CI pipelines, Terraform providers, and SDK-driven integrations. Each carries its own RBAC role and a rotatable API token — distinct from a human admin's personal API keys."
      showInactive={showInactive}
      onToggleShowInactive={(next) =>
        void navigate({
          to: "/automation-accounts",
          search: next ? { show_inactive: true } : {},
        })
      }
      onNewAutomationAccount={() => void navigate({ to: "/automation-accounts/new" })}
      renderDetailLink={({ id, className, children, ...rest }) => (
        <Link
          to="/automation-accounts/$id"
          params={{ id }}
          className={className}
          aria-label={rest["aria-label"]}
        >
          {children}
        </Link>
      )}
    />
  );
}

export const Route = createFileRoute("/_authed/automation-accounts/")({
  validateSearch: (raw: Record<string, unknown>): AutomationAccountsListSearch =>
    automationAccountsListSearchSchema.parse(raw),
  loaderDeps: ({ search }) => ({ showInactive: !!search.show_inactive }),
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData({
      queryKey: [
        ...queryKeys.automationAccounts.list(scopeKey(null)),
        { showInactive: deps.showInactive },
      ] as const,
      queryFn: () =>
        fetchAutomationAccounts(null, { showInactive: deps.showInactive }),
    }),
  component: PlatformAutomationAccountsPage,
});
